import './App.css';
import React, { useState, useEffect, useCallback } from 'react';
import MarkdownViewer from './component/MarkdownViewer';

const GITHUB_REPO_URL = 'https://github.com/caffinism/park-log';
const SITE_URL = 'https://log.zziny.com/';
const AVATAR_URL = 'https://avatars.githubusercontent.com/u/45790156?s=48&v=4';

const REPO_META = {
  watchCount: 1,
  forkCount: 0,
  starCount: 1,
  description: 'Park Young Jae (박영재) — DevOps engineer portfolio.',
  topics: ['portfolio', 'devops', 'aws', 'resume'],
};

const CONTRIBUTORS = [
  { name: 'caffinism', displayName: 'YoungJae Park', avatar: AVATAR_URL, url: 'https://github.com/caffinism' },
];

const LANGUAGES = [
  { name: 'JavaScript', percent: 52.3, color: '#f1e05a' },
  { name: 'CSS',        percent: 43.7, color: '#563d7c' },
  { name: 'HTML',       percent: 4.0,  color: '#e34c26' },
];

const SECTIONS = [
  { id: 'Intro',   label: 'Intro',   commit: 'add my introduction', date: '2026-04-22' },
  { id: 'Profile', label: 'Profile', commit: 'add my profile',      date: '2026-04-17' },
  { id: 'Skill',   label: 'Skill',   commit: 'add my skill',        date: '2026-04-08' },
  { id: 'Project', label: 'Project', commit: 'add my project',      date: '2026-03-20' },
];

const CASES_FOLDER = { id: 'cases', label: 'cases', commit: 'add case studies', date: '2026-04-23' };

const CASES = [
  { id: 'cases/crm-bonus-calculation',        label: 'crm-bonus-calculation.md',        commit: '누적 반올림 오차 분석과 고객 설득',              date: '2022-09-30' },
  { id: 'cases/partner-site-cost-optimization', label: 'partner-site-cost-optimization.md', commit: '인수받은 인프라의 비용 누수 탐지와 자동화',   date: '2024-04-30' },
  { id: 'cases/argo-rollouts-automode',        label: 'argo-rollouts-automode.md',        commit: 'EKS AutoMode 환경에서 무중단 배포 완성하기',    date: '2025-08-31' },
  { id: 'cases/vpc-cni-prefix-delegation',     label: 'vpc-cni-prefix-delegation.md',     commit: 'VPC CNI Prefix Delegation과 서브넷 크기의 트레이드오프', date: '2025-12-31' },
  { id: 'cases/performance-test-chatbot',      label: 'performance-test-chatbot.md',      commit: '비전문가의 성능 테스트 설계와 병목 분석',        date: '2025-12-31' },
];

const DEFAULT_SECTION = 'Intro';

const FileIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" className="octicon">
    <path d="M2 1.75C2 .784 2.784 0 3.75 0h6.586c.464 0 .909.184 1.237.513l2.914 2.914c.329.328.513.773.513 1.237v9.586A1.75 1.75 0 0 1 13.25 16h-9.5A1.75 1.75 0 0 1 2 14.25Zm1.75-.25a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h9.5a.25.25 0 0 0 .25-.25V6h-2.75A1.75 1.75 0 0 1 9 4.25V1.5Zm6.75.062V4.25c0 .138.112.25.25.25h2.688l-.011-.013-2.914-2.914-.013-.011Z" />
  </svg>
);

const FolderIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" className="octicon octicon-folder">
    <path d="M1.75 1A1.75 1.75 0 0 0 0 2.75v10.5C0 14.216.784 15 1.75 15h12.5A1.75 1.75 0 0 0 16 13.25v-8.5A1.75 1.75 0 0 0 14.25 3H7.5a.25.25 0 0 1-.2-.1l-.9-1.2C6.07 1.26 5.55 1 5 1H1.75Z" />
  </svg>
);

const BookIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" className="octicon">
    <path d="M0 1.75A.75.75 0 0 1 .75 1h4.253c1.227 0 2.317.59 3 1.501A3.743 3.743 0 0 1 11.006 1h4.245a.75.75 0 0 1 .75.75v10.5a.75.75 0 0 1-.75.75h-4.507a2.25 2.25 0 0 0-1.591.659l-.622.621a.75.75 0 0 1-1.06 0l-.622-.621A2.25 2.25 0 0 0 5.258 13H.75a.75.75 0 0 1-.75-.75Zm7.251 10.324.004-5.073-.002-2.253A2.25 2.25 0 0 0 5.003 2.5H1.5v9h3.757a3.75 3.75 0 0 1 1.994.574ZM8.755 4.75l-.004 7.322a3.752 3.752 0 0 1 1.992-.572H14.5v-9h-3.495a2.25 2.25 0 0 0-2.25 2.25Z" />
  </svg>
);

function getHashView() {
  const hash = window.location.hash.replace('#', '');
  if (!hash) return DEFAULT_SECTION;
  if (hash === 'cases') return 'cases';
  if (hash.startsWith('cases/') && CASES.some((c) => c.id === hash)) return hash;
  return SECTIONS.find((s) => s.id === hash)?.id ?? DEFAULT_SECTION;
}

function formatRelative(dateStr) {
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7)   return `${days} days ago`;
  if (days < 14)  return 'last week';
  if (days < 30)  return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60)  return 'last month';
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  if (days < 548) return 'last year';
  return `${Math.floor(days / 365)} years ago`;
}

function formatDateTitle(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function RelativeDate({ date }) {
  return <time dateTime={date} title={formatDateTitle(date)}>{formatRelative(date)}</time>;
}

function formatPercent(n) {
  return `${n.toFixed(1)}%`;
}

function App() {
  const [activeView, setActiveView] = useState(getHashView);

  const navigate = useCallback((view) => {
    window.location.hash = view;
    setActiveView(view);
  }, []);

  useEffect(() => {
    const onHashChange = () => setActiveView(getHashView());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    const section = SECTIONS.find((s) => s.id === activeView);
    const caseFile = CASES.find((c) => c.id === activeView);
    if (section) document.title = `${section.label} — Park's Log`;
    else if (caseFile) document.title = `${caseFile.label} — Park's Log`;
    else if (activeView === 'cases') document.title = `cases/ — Park's Log`;
    else document.title = "Park's Log";
  }, [activeView]);

  const inCasesContext = activeView === 'cases' || activeView.startsWith('cases/');
  const activeCaseFile = CASES.find((c) => c.id === activeView);
  const activeSectionFile = SECTIONS.find((s) => s.id === activeView);
  const activeFile = activeSectionFile || activeCaseFile || (activeView === 'cases' ? CASES_FOLDER : SECTIONS[0]);
  const showMarkdown = activeView !== 'cases';
  const rawUrl = `${process.env.PUBLIC_URL || ''}/markdown/${activeView}.md`;

  return (
    <div className="App">
      <header id="header">
        <div id="headerContainer">
          <div id="titleContainer">
            <div id="titleComponent">
              <img
                id="titleImage"
                alt="Park Young Jae avatar"
                width="24"
                height="24"
                src={AVATAR_URL}
              />
              <strong id="titleStrong">
                <a id="title" href={SITE_URL}>
                  Park's Log
                </a>
              </strong>
              <span id="titlePublic">Public</span>
            </div>
          </div>
          <nav id="detailsContainer" aria-label="Repository actions">
            <ul id="detailsUl">
              <li className="detailsLi">
                <a href={GITHUB_REPO_URL} aria-label="Watch this repository" target="_blank" rel="noopener noreferrer">
                  <svg aria-hidden="true" height="16" viewBox="0 0 16 16" width="16">
                    <path d="M8 2c1.981 0 3.671.992 4.933 2.078 1.27 1.091 2.187 2.345 2.637 3.023a1.62 1.62 0 0 1 0 1.798c-.45.678-1.367 1.932-2.637 3.023C11.67 13.008 9.981 14 8 14c-1.981 0-3.671-.992-4.933-2.078C1.797 10.83.88 9.576.43 8.898a1.62 1.62 0 0 1 0-1.798c.45-.677 1.367-1.931 2.637-3.022C4.33 2.992 6.019 2 8 2ZM1.679 7.932a.12.12 0 0 0 0 .136c.411.622 1.241 1.75 2.366 2.717C5.176 11.758 6.527 12.5 8 12.5c1.473 0 2.825-.742 3.955-1.715 1.124-.967 1.954-2.096 2.366-2.717a.12.12 0 0 0 0-.136c-.412-.621-1.242-1.75-2.366-2.717C10.824 4.242 9.473 3.5 8 3.5c-1.473 0-2.825.742-3.955 1.715-1.124.967-1.954 2.096-2.366 2.717ZM8 10a2 2 0 1 1-.001-3.999A2 2 0 0 1 8 10Z" />
                  </svg>
                  Watch
                  <span className="btnCounter">{REPO_META.watchCount}</span>
                </a>
              </li>
              <li className="detailsLi">
                <a href={`${GITHUB_REPO_URL}/fork`} aria-label="Fork this repository" target="_blank" rel="noopener noreferrer">
                  <svg aria-hidden="true" height="16" viewBox="0 0 16 16" width="16">
                    <path d="M5 5.372v.878c0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75v-.878a2.25 2.25 0 1 1 1.5 0v.878a2.25 2.25 0 0 1-2.25 2.25h-1.5v2.128a2.251 2.251 0 1 1-1.5 0V8.5h-1.5A2.25 2.25 0 0 1 3.5 6.25v-.878a2.25 2.25 0 1 1 1.5 0ZM5 3.25a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Zm6.75.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm-3 8.75a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z" />
                  </svg>
                  Fork
                  <span className="btnCounter">{REPO_META.forkCount}</span>
                </a>
              </li>
              <li className="detailsLi">
                <a href={GITHUB_REPO_URL} aria-label="Star this repository" target="_blank" rel="noopener noreferrer">
                  <svg aria-hidden="true" height="16" viewBox="0 0 16 16" width="16">
                    <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.751.751 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Zm0 2.445L6.615 5.5a.75.75 0 0 1-.564.41l-3.097.45 2.24 2.184a.75.75 0 0 1 .216.664l-.528 3.084 2.769-1.456a.75.75 0 0 1 .698 0l2.77 1.456-.53-3.084a.75.75 0 0 1 .216-.664l2.24-2.183-3.096-.45a.75.75 0 0 1-.564-.41L8 2.694Z" />
                  </svg>
                  Star
                  <span className="btnCounter">{REPO_META.starCount}</span>
                </a>
              </li>
            </ul>
          </nav>
        </div>
        <hr id="headerHr" />
      </header>

      <div id="content">
        <div id="contentGrid">
          <div id="mainLayout">
            <div id="mainBox">
              <div id="mainHeader">
                <div id="mainHeaderLeft">
                  <a href={`${GITHUB_REPO_URL}/tree/main`} aria-label="Current branch: main" target="_blank" rel="noopener noreferrer">
                    <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
                      <path d="M9.5 3.25a2.25 2.25 0 1 1 3 2.122V6A2.5 2.5 0 0 1 10 8.5H6a1 1 0 0 0-1 1v1.128a2.251 2.251 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.5 0v1.836A2.493 2.493 0 0 1 6 7h4a1 1 0 0 0 1-1v-.628A2.25 2.25 0 0 1 9.5 3.25Zm-6 0a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0Zm8.25-.75a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5ZM4.25 12a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Z" />
                    </svg>
                    main
                  </a>
                </div>
                <div id="mainHeaderRight">
                  <a href={GITHUB_REPO_URL} aria-label="View source code on GitHub" target="_blank" rel="noopener noreferrer">
                    <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
                      <path d="m11.28 3.22 4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.749.749 0 0 1-1.275-.326.749.749 0 0 1 .215-.734L13.94 8l-3.72-3.72a.749.749 0 0 1 .326-1.275.749.749 0 0 1 .734.215Zm-6.56 0a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042L2.06 8l3.72 3.72a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215L.47 8.53a.75.75 0 0 1 0-1.06Z" />
                    </svg>
                    Code
                  </a>
                </div>
              </div>

              <div id="file">
                <table id="mainTable">
                  <colgroup>
                    <col className="col-icon" />
                    <col className="col-name" />
                    <col className="col-commit" />
                    <col className="col-date" />
                  </colgroup>
                  <tbody id="mainTbody">
                    <tr id="mainHeadTr">
                      <td colSpan="3">
                        <img
                          alt="park avatar"
                          height="20"
                          width="20"
                          src="https://github.githubassets.com/images/gravatars/gravatar-user-420.png?size=40"
                        />
                        <span>park</span>
                        <a href={`#${activeView}`}>{activeFile?.commit}</a>
                      </td>
                      <td className="mainHeadTdDate">{activeFile && <RelativeDate date={activeFile.date} />}</td>
                    </tr>
                    {inCasesContext ? (
                      <>
                        <tr className="mainTr">
                          <td className="mainTd1" colSpan="2">
                            <FolderIcon />
                            <a href="#Project" onClick={(e) => { e.preventDefault(); navigate('Project'); }}>
                              ..
                            </a>
                          </td>
                          <td className="mainTd2" />
                          <td className="mainTd3" />
                        </tr>
                        {CASES.map((c) => (
                          <tr key={c.id} className={`mainTr${activeView === c.id ? ' mainTr--active' : ''}`}>
                            <td className="mainTd1" colSpan="2">
                              <FileIcon />
                              <a href={`#${c.id}`} onClick={(e) => { e.preventDefault(); navigate(c.id); }}>
                                {c.label}
                              </a>
                            </td>
                            <td className="mainTd2">{c.commit}</td>
                            <td className="mainTd3"><RelativeDate date={c.date} /></td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <>
                        {SECTIONS.map((section) => (
                          <tr key={section.id} className={`mainTr${activeView === section.id ? ' mainTr--active' : ''}`}>
                            <td className="mainTd1" colSpan="2">
                              <FileIcon />
                              <a href={`#${section.id}`} onClick={(e) => { e.preventDefault(); navigate(section.id); }}>
                                {section.label}
                              </a>
                            </td>
                            <td className="mainTd2">{section.commit}</td>
                            <td className="mainTd3"><RelativeDate date={section.date} /></td>
                          </tr>
                        ))}
                        <tr className="mainTr">
                          <td className="mainTd1" colSpan="2">
                            <FolderIcon />
                            <a href="#cases" onClick={(e) => { e.preventDefault(); navigate('cases'); }}>
                              cases
                            </a>
                          </td>
                          <td className="mainTd2">{CASES_FOLDER.commit}</td>
                          <td className="mainTd3"><RelativeDate date={CASES_FOLDER.date} /></td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {showMarkdown && (
                <main id="markdown">
                  <div className="markdownBox">
                    <div className="markdownBoxHeader">
                      <div className="markdownBoxHeaderLeft">
                        <BookIcon />
                        <span className="markdownFilename">{activeView.includes('/') ? activeView.split('/').pop() : activeView}.md</span>
                      </div>
                      <div className="markdownBoxHeaderRight">
                        <a
                          className="markdownBoxAction"
                          href={rawUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Raw
                        </a>
                      </div>
                    </div>
                    <div className="markdownBoxBody">
                      <MarkdownViewer filename={activeView} />
                    </div>
                  </div>
                </main>
              )}
            </div>
          </div>

          <div id="sidebarLayout">
            <div id="sidebarBox">
              <aside id="right">

                <section className="rightBox">
                  <h2 className="rightBoxTitle">About</h2>
                  <p className="rightBoxDesc">{REPO_META.description}</p>
                  <p className="rightBoxLink">
                    <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" className="octicon">
                      <path d="m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z" />
                    </svg>
                    <a href={SITE_URL} target="_blank" rel="noopener noreferrer">log.zziny.com</a>
                  </p>
                  <div className="topicList">
                    {REPO_META.topics.map((t) => (
                      <span key={t} className="topicTag">{t}</span>
                    ))}
                  </div>
                </section>

                <hr className="sidebarHr" />

                <section className="rightBox">
                  <h2 className="rightBoxTitle">Releases</h2>
                  <div className="rightBoxEmpty">
                    <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" className="octicon">
                      <path d="M7.75 14A1.75 1.75 0 0 1 6 12.25v-8.5C6 2.784 6.784 2 7.75 2h6.5c.966 0 1.75.784 1.75 1.75v8.5A1.75 1.75 0 0 1 14.25 14Zm-.25-1.75c0 .138.112.25.25.25h6.5a.25.25 0 0 0 .25-.25v-8.5a.25.25 0 0 0-.25-.25h-6.5a.25.25 0 0 0-.25.25ZM4.9 3.508a.75.75 0 0 1-.274 1.025.25.25 0 0 0-.126.217v6.5a.25.25 0 0 0 .126.217.75.75 0 0 1-.752 1.298A1.75 1.75 0 0 1 3 11.25v-6.5c0-.649.353-1.214.874-1.516a.75.75 0 0 1 1.025.274ZM1.625 5.533h.001a.25.25 0 0 0-.126.217v4.5a.25.25 0 0 0 .126.217.75.75 0 0 1-.752 1.298A1.75 1.75 0 0 1 0 10.25v-4.5a1.75 1.75 0 0 1 .873-1.516.75.75 0 1 1 .752 1.299Z" />
                    </svg>
                    <span>No releases published</span>
                  </div>
                </section>

                <hr className="sidebarHr" />

                <section className="rightBox">
                  <h2 className="rightBoxTitle">Packages</h2>
                  <div className="rightBoxEmpty">
                    <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" className="octicon">
                      <path d="m8.878.392 5.25 3.045c.54.314.872.89.872 1.514v6.098a1.75 1.75 0 0 1-.872 1.514l-5.25 3.045a1.75 1.75 0 0 1-1.756 0l-5.25-3.045A1.75 1.75 0 0 1 1 11.049V4.951c0-.624.332-1.201.872-1.514L7.122.392a1.75 1.75 0 0 1 1.756 0ZM7.875 1.69l-4.63 2.685L8 7.133l4.755-2.758-4.63-2.685a.248.248 0 0 0-.25 0ZM2.5 5.677v5.372c0 .09.047.171.125.216l4.625 2.683V8.432Zm6.25 8.271 4.625-2.683a.25.25 0 0 0 .125-.216V5.677L8.75 8.432Z" />
                    </svg>
                    <span>No packages published</span>
                  </div>
                </section>

                <hr className="sidebarHr" />

                <section className="rightBox">
                  <h2 className="rightBoxTitle">
                    Contributors <span className="rightBoxCounter">{CONTRIBUTORS.length}</span>
                  </h2>
                  <ul className="contributorsList">
                    {CONTRIBUTORS.map((c) => (
                      <li key={c.name}>
                        <a href={c.url} target="_blank" rel="noopener noreferrer" aria-label={`${c.name} (${c.displayName})`}>
                          <img src={c.avatar} alt={c.name} width="32" height="32" />
                        </a>
                        <div className="contributorInfo">
                          <span className="contributorHandle">{c.name}</span>
                          <span className="contributorDisplayName">{c.displayName}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>

                <hr className="sidebarHr" />

                <section className="rightBox">
                  <h2 className="rightBoxTitle">Languages</h2>
                  <div className="languagesBar" role="img" aria-label="Languages breakdown">
                    {LANGUAGES.map((lang) => (
                      <span
                        key={lang.name}
                        className="languagesBarSegment"
                        style={{ width: `${lang.percent}%`, backgroundColor: lang.color }}
                      />
                    ))}
                  </div>
                  <ul className="languagesList">
                    {LANGUAGES.map((lang) => (
                      <li key={lang.name}>
                        <span className="languagesDot" style={{ backgroundColor: lang.color }} />
                        <span className="languagesName">{lang.name}</span>
                        <span className="languagesPercent">{formatPercent(lang.percent)}</span>
                      </li>
                    ))}
                  </ul>
                </section>

              </aside>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
